import React, { useEffect, useMemo, useState } from 'react';
import { Music2 } from 'lucide-react';
import { parseTrackTitle } from '@meradio/music-utils';
import { IconButton, Select } from '@/components/ui';
import { useAppSelector } from '@/store/store';
import { useDecks } from '@/features/player/PlayerProvider';
import { selectAudibleDeck, type DeckId } from '@/features/player/decksSlice';
import { getSets, getSet, getSetSuggestions, type DjSet } from '@/services/sets.api';
import { getCrates, getCrate, type Crate } from '@/services/crates.api';
import { DeckRowVideo, toDeckVideo, featuresOf, metaBpm, metaKey, metaCamelot } from '../trackMeta';

type DrawerTab = 'sets' | 'crates' | 'rail';

const TABS: { id: DrawerTab; label: string }[] = [
  { id: 'sets', label: 'Sets' },
  { id: 'crates', label: 'Crates' },
  { id: 'rail', label: 'Mixes well next' },
];

const BrowserDrawer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DrawerTab>('sets');
  const [sets, setSets] = useState<DjSet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState('');
  const [setRows, setSetRows] = useState<DeckRowVideo[]>([]);
  const [crates, setCrates] = useState<Crate[]>([]);
  const [selectedCrateId, setSelectedCrateId] = useState('');
  const [crateRows, setCrateRows] = useState<DeckRowVideo[]>([]);
  const [railRows, setRailRows] = useState<DeckRowVideo[]>([]);

  const audibleDeck = useAppSelector(selectAudibleDeck);
  const { decks, loadToDeck } = useDecks();

  const audibleVideoId = decks[audibleDeck].video?.id ?? null;
  const excludeKey = useMemo(() => {
    const ids: string[] = [];
    if (decks.a.video) ids.push(decks.a.video.id);
    if (decks.b.video) ids.push(decks.b.video.id);
    return ids.join(',');
  }, [decks.a.video, decks.b.video]);

  useEffect(() => {
    let cancelled = false;
    getSets().then((result) => { if (!cancelled) setSets(result); }).catch(() => undefined);
    getCrates().then((result) => { if (!cancelled) setCrates(result); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedSetId) {
      setSetRows([]);
      return;
    }
    let cancelled = false;
    getSet(selectedSetId)
      .then((set) => {
        if (cancelled) return;
        const rows = [...(set.entries ?? [])]
          .sort((a, b) => a.position - b.position)
          .map((entry) => entry.video as DeckRowVideo);
        setSetRows(rows);
      })
      .catch(() => { if (!cancelled) setSetRows([]); });
    return () => { cancelled = true; };
  }, [selectedSetId]);

  useEffect(() => {
    if (!selectedCrateId) {
      setCrateRows([]);
      return;
    }
    let cancelled = false;
    getCrate(selectedCrateId)
      .then((crate) => { if (!cancelled) setCrateRows((crate.videos ?? []) as DeckRowVideo[]); })
      .catch(() => { if (!cancelled) setCrateRows([]); });
    return () => { cancelled = true; };
  }, [selectedCrateId]);

  useEffect(() => {
    if (activeTab !== 'rail' || !audibleVideoId) {
      setRailRows([]);
      return;
    }
    let cancelled = false;
    getSetSuggestions({ videoId: audibleVideoId, exclude: excludeKey ? excludeKey.split(',') : [] })
      .then((result) => {
        if (cancelled) return;
        const rows = [...result.compatible, ...result.wildcards].map((suggestion) => ({
          ...suggestion.video,
          audioFeatures: suggestion.audioFeatures ?? suggestion.video.audioFeatures ?? null,
        })) as DeckRowVideo[];
        setRailRows(rows);
      })
      .catch(() => { if (!cancelled) setRailRows([]); });
    return () => { cancelled = true; };
  }, [activeTab, audibleVideoId, excludeKey]);

  const load = (deck: DeckId, video: DeckRowVideo) => loadToDeck(deck, toDeckVideo(video));

  const renderRow = (video: DeckRowVideo) => {
    const parsed = parseTrackTitle(video.title);
    const features = featuresOf(video);
    return (
      <li
        key={video.videoId}
        className="flex items-center gap-3 px-3 py-2 hover:bg-meradio-light/20 dark:hover:bg-bg-dark-tertiary/50 transition-colors"
      >
        <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-meradio-light dark:bg-bg-dark-primary">
          {video.thumbnailUrl ? (
            <img src={video.thumbnailUrl} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music2 className="w-4 h-4 text-meradio-dark-light dark:text-text-dark-tertiary" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary truncate">
            {parsed.track}
          </p>
          {features && (
            <p className="text-xs text-meradio-dark-light dark:text-text-dark-secondary truncate">
              {metaBpm(features)} · {metaKey(features)} · {metaCamelot(features)}
            </p>
          )}
        </div>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Load to deck A"
          onClick={() => load('a', video)}
          className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary font-semibold"
        >
          → A
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Load to deck B"
          onClick={() => load('b', video)}
          className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary font-semibold"
        >
          → B
        </IconButton>
      </li>
    );
  };

  const emptyNote = (message: string) => (
    <p className="px-3 py-8 text-center text-sm text-meradio-dark-light dark:text-text-dark-tertiary">
      {message}
    </p>
  );

  return (
    <div className="bg-white dark:bg-bg-dark-secondary rounded-xl border border-meradio-light dark:border-border-dark overflow-hidden">
      <div className="border-b border-meradio-light dark:border-border-dark px-4">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-meradio-primary text-meradio-primary'
                  : 'border-transparent text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-dark dark:hover:text-text-dark-primary hover:border-meradio-light'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-4">
        {activeTab === 'sets' && (
          <div className="space-y-3">
            <Select
              value={selectedSetId}
              aria-label="Choose a set"
              onChange={(event) => setSelectedSetId(event.target.value)}
              className="w-full"
            >
              <option value="">Choose a set</option>
              {sets.map((set) => (
                <option key={set.id} value={set.id}>{set.name}</option>
              ))}
            </Select>
            {setRows.length > 0 ? (
              <ul className="divide-y divide-meradio-light dark:divide-border-dark">{setRows.map(renderRow)}</ul>
            ) : (
              emptyNote('Pick a set to load its tracks.')
            )}
          </div>
        )}

        {activeTab === 'crates' && (
          <div className="space-y-3">
            <Select
              value={selectedCrateId}
              aria-label="Choose a crate"
              onChange={(event) => setSelectedCrateId(event.target.value)}
              className="w-full"
            >
              <option value="">Choose a crate</option>
              {crates.map((crate) => (
                <option key={crate.id} value={crate.id}>{crate.name}</option>
              ))}
            </Select>
            {crateRows.length > 0 ? (
              <ul className="divide-y divide-meradio-light dark:divide-border-dark">{crateRows.map(renderRow)}</ul>
            ) : (
              emptyNote('Pick a crate to load its tracks.')
            )}
          </div>
        )}

        {activeTab === 'rail' && (
          railRows.length > 0 ? (
            <ul className="divide-y divide-meradio-light dark:divide-border-dark">{railRows.map(renderRow)}</ul>
          ) : (
            emptyNote(audibleVideoId ? 'No compatible tracks found yet.' : 'Load a track to see what mixes well next.')
          )
        )}
      </div>
    </div>
  );
};

export default BrowserDrawer;
